import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useFadeIn } from '../components/useFadeIn';
import ServicesHero from '../Images/serviceshero.png';
import CarbonCredit from '../Images/carboncredit.jpg';
import AiDriven from '../Images/aidriven.jpg';
import RealTime from '../Images/realtime.jpg';
import Benchmarking from '../Images/benchmarking.jpg';

function FadeSection({ children, className = '' }) {
  const ref = useFadeIn();
  return <div ref={ref} className={`section-fade ${className}`}>{children}</div>;
}

const services = [
  {
    title: 'AI-Driven Sustainability Solutions',
    desc: 'Carbonetics delivers automated, real-time emissions tracking, eliminating manual reporting delays and providing actionable insights.',
    bullets: [
      'Captures emissions data from IoT sensors, ERP systems, and third-party sources.',
      'Monitor carbon footprints in real time across operations and supply chains.',
      'AI-driven insights pinpoint high-emission areas for optimization and cost savings.',
    ],
    image: AiDriven,
  },
  {
    title: 'AI-Powered Predictive Analytics',
    desc: 'Carbonetics uses AI-driven simulations to help businesses predict, plan, and optimize sustainability strategies before implementation, ensuring maximum efficiency and cost savings.',
    bullets: [
      'Forecast the impact of sustainability initiatives before execution',
      'Model process changes and equipment upgrades for efficiency.',
      'Reduce emissions by up to 20% in three years.',
    ],
    image: RealTime,
  },
  {
    title: 'Compliance & ESG Reporting Automation',
    desc: 'Carbonetics simplifies ESG compliance with automated reporting, real-time alerts, and benchmarking tools, ensuring businesses meet global regulations effortlessly.',
    bullets: [
      'Instantly generate reports aligned with TCFD, CSSB, and EU ETS.',
      'Stay updated on regulatory deadlines and policy changes.',
      'Compare emissions performance with industry leaders.',
    ],
    image: Benchmarking,
  },
  {
    title: 'Carbon Credit Management & Trading',
    desc: 'Carbonetics helps businesses verify, trade, and optimize carbon credits, making sustainability a profitable opportunity while ensuring regulatory compliance.',
    bullets: [
      'Buy, sell, and monetize emissions reductions.',
      'Stay compliant with cap-and-trade programs.',
      'Maximize financial returns on carbon reduction efforts.',
    ],
    image: CarbonCredit,
  },
];

const additionalFeatures = [
  { title: 'Energy Consumption Optimization', desc: 'Monitor real-time energy usage across facilities, detect high-consumption areas, and implement AI-driven strategies to reduce waste while maintaining productivity.' },
  { title: 'Lifecycle Emissions Analysis', desc: 'Track carbon impact at every stage of production, from raw material sourcing to product disposal, ensuring sustainability efforts extend throughout the entire lifecycle.' },
  { title: 'Automated Sustainability Forecasting', desc: 'Simulate different sustainability strategies, predict their long-term effects on emissions and costs, and optimize investments in green technology for maximum impact.' },
  { title: 'Supply Chain Carbon Visibility', desc: 'Assess and monitor the carbon footprint of your entire supply chain, ensuring vendors meet sustainability standards while optimizing logistics for lower emissions and better efficiency.' },
];

const faqs = [
  { q: 'What is Carbonetics, and how does it help businesses?' },
  { q: 'How does Carbonetics support carbon credit management?' },
  { q: 'Can Carbonetics predict environmental impact?' },
  { q: 'How does compliance benchmarking work?' },
  { q: 'What industries can benefit from Carbonetics?' },
];

export default function Services() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <main>
      {/* Hero */}
      <section className="bg-[#2d3828] py-32 sm:py-40 px-4 sm:px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 sm:mb-10">
            AI-Powered Sustainability,<br className="hidden sm:block" /> Built for Impact.
          </h1>

          {/* Hero Image */}
          <div className="mb-8 sm:mb-10 rounded-2xl overflow-hidden">
            <img
              src={ServicesHero}
              alt="Services"
              className="w-full object-cover"
            />
          </div>

          <p className="font-body text-white/60 text-base sm:text-lg mb-8 sm:mb-10">
            Carbonetics transforms industrial operations by enabling actionable, data-driven decision-making.
          </p>
          <Link to="/contact"
            className="inline-flex items-center gap-2 border border-white text-white font-body px-6 sm:px-8 py-3 sm:py-3.5 rounded-full hover:bg-white hover:text-[#2d3828] transition-all text-sm sm:text-base">
            Get Started <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Sustainability That Works */}
      <section className="bg-[#f4f7f0] py-14 sm:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <FadeSection>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1e3019] mb-4 sm:mb-6">
              Sustainability That Works for Your Business
            </h2>
            <p className="font-body text-[#1e3019]/60 text-sm sm:text-base leading-relaxed max-w-xl">
              Carbonetics helps businesses track, optimize, and monetize sustainability efforts with real-time data and AI-driven insights. Move beyond compliance and make sustainability a competitive edge.
            </p>
          </FadeSection>
        </div>
      </section>

      {/* Main Services */}
      {services.map((s, i) => (
        <section key={i} className={`py-14 sm:py-20 px-4 sm:px-6 ${i % 2 === 0 ? 'bg-[#f4f7f0]' : 'bg-[#eaf0e6]'}`}>
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center">
            <FadeSection className={i % 2 !== 0 ? 'md:order-2' : ''}>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-[#1e3019] mb-4 sm:mb-6">{s.title}</h2>
              <p className="font-body text-[#1e3019]/60 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">{s.desc}</p>
              <ul className="flex flex-col gap-2 sm:gap-3">
                {s.bullets.map((b, j) => (
                  <li key={j} className="flex items-start gap-3 font-body text-xs sm:text-sm text-[#1e3019]/70">
                    <span className="w-2 h-2 rounded-full bg-[#2d3828] shrink-0 mt-1.5" />
                    {b}
                  </li>
                ))}
              </ul>
            </FadeSection>
            <FadeSection className={i % 2 !== 0 ? 'md:order-1' : ''}>
              <div className="rounded-2xl overflow-hidden h-48 sm:h-72">
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </FadeSection>
          </div>
        </section>
      ))}

      {/* Additional Features */}
      <section className="bg-[#f4f7f0] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <FadeSection>
            <p className="font-body text-[#1e3019] text-base sm:text-lg md:text-2xl font-medium leading-relaxed max-w-3xl mb-10 sm:mb-16">
              Carbonetics goes beyond tracking emissions—it provides data-driven intelligence to help businesses optimize energy use, forecast sustainability outcomes, and build a more transparent, efficient, and profitable sustainability strategy.
            </p>
          </FadeSection>
          <div className="flex flex-col gap-0">
            {additionalFeatures.map((f, i) => (
              <FadeSection key={i}>
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 py-6 sm:py-8 border-b border-[#1e3019]/10">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#2d3828]/10 flex items-center justify-center shrink-0">
                    <div className="w-3 h-3 rounded-full bg-[#2d3828]/30" />
                  </div>
                  <div>
                    <h3 className="font-body font-semibold text-[#1e3019] text-sm sm:text-base mb-1 sm:mb-2">{f.title}</h3>
                    <p className="font-body text-[#1e3019]/50 text-xs sm:text-sm leading-relaxed max-w-2xl">{f.desc}</p>
                  </div>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#f4f7f0] py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <FadeSection>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1e3019] mb-8 sm:mb-12">FAQ</h2>
          </FadeSection>
          <div className="flex flex-col gap-0">
            {faqs.map((faq, i) => (
              <FadeSection key={i}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between py-4 sm:py-5 border-b border-[#1e3019]/10 text-left">
                  <span className="font-body text-[#1e3019] text-sm sm:text-base font-medium pr-4">{faq.q}</span>
                  <span className="font-body text-[#1e3019]/40 text-lg sm:text-xl shrink-0">{openFaq === i ? '−' : '∨'}</span>
                </button>
                {openFaq === i && (
                  <div className="py-3 sm:py-4 font-body text-[#1e3019]/60 text-xs sm:text-sm leading-relaxed">
                    Please contact us for detailed information about this topic.
                  </div>
                )}
              </FadeSection>
            ))}
          </div>

          <FadeSection className="mt-12 sm:mt-16">
            <p className="font-body text-[#1e3019]/60 text-sm mb-4 sm:mb-6">
              Do you have any questions about how Carbonetics can transform your supply chain operations?
            </p>
            <Link to="/contact"
              className="inline-flex items-center gap-2 border border-[#1e3019] text-[#1e3019] font-body px-5 sm:px-6 py-2.5 sm:py-3 rounded-full hover:bg-[#1e3019] hover:text-white transition-all text-xs sm:text-sm">
              Get in touch <ArrowRight size={14} />
            </Link>
          </FadeSection>
        </div>
      </section>
    </main>
  );
}